import React, { useState, useEffect, useRef } from 'react';


// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';



const DialogStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  dialog: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    width: '700px',
    maxWidth: '90vw',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  title: {
    fontSize: '18px',
    textAlign: 'center',
    margin: 0,
    color: '#000',
    backgroundColor: '#CCCCFF',
    padding: '10px',
    borderRadius: '4px'
  }
};

const CreateProductDialog = ({
  isOpen,
  onClose,
  onSubmit,
  corporateAccount,
  projectId,
  currentProject,
  isEditMode,
  productToEdit,
  userId // Add userId prop
}) => {
  const [productName, setProductName] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [productCompany, setProductCompany] = useState('');
  const [productVersion, setProductVersion] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [permissions, setPermissions] = useState({
    canCreateOrEdit: false
  });
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const inputRef = useRef(null);

  const login_token = localStorage.getItem('authToken');

  // Check permissions function
  const checkPermission = async () => {
    console.log('=== checkPermission called ===');

    if (!corporateAccount || !userId || !projectId || !login_token) {
      console.log('Missing required props in checkPermission:', {
        corporateAccount: !!corporateAccount,
        userId: !!userId,
        projectId: !!projectId,
        login_token: !!login_token
      });
      setPermissions({ canCreateOrEdit: true }); // Default to allow access
      setPermissionsLoaded(true);
      return false;
    }

    try {
      console.log('About to make API call with:', {
        corporateAccount,
        userId,
        projectId,
        category_header: "Products Definition"
      });

      const response = await fetch(appConfig.getUrl('/get_user_access_level_new'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          user_id: userId,
          project_id: projectId,
          category_header: "Products Definition",
          category_sub_header: []
        }),
      });

      console.log('API response received:', response.status, response.statusText);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Permissions API response data:', data);

      // Create new permissions object
      const newPermissions = { canCreateOrEdit: false };

      // Map API response to permission fields
      if (data.user_actions_list && Array.isArray(data.user_actions_list)) {
        data.user_actions_list.forEach(item => {
          switch(item.user_action) {
            case 'Create Or Edit Products':
              newPermissions.canCreateOrEdit = item.has_access;
             break;
          }
        });
      }

      console.log('Final permissions set:', newPermissions);
      setPermissions(newPermissions);
      setPermissionsLoaded(true);

      return data.access_status && data.status === 'Success';
    } catch (error) {
      console.error('=== Permission check error ===');
      console.error('Error details:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);

      // Set default permissions on error - allow access so user isn't blocked
      setPermissions({ canCreateOrEdit: true });
      setPermissionsLoaded(true);
      return false;
    }
  };

  // Check permissions when dialog opens and required props are available
  useEffect(() => {
    if (isOpen) {
      console.log('=== DIALOG OPENED ===');
      console.log('Props check:', {
        isOpen,
        corporateAccount: !!corporateAccount,
        userId: !!userId,
        projectId: !!projectId,
        login_token: !!login_token
      });

      if (corporateAccount && userId && projectId && login_token) {
        console.log('All props available, calling checkPermission...');
        checkPermission();
      } else {
        console.log('Missing props, setting default permissions');
        console.log('Details:', { corporateAccount, userId, projectId, hasToken: !!login_token });
        // If props are missing, set default permissions to avoid blocking
        setPermissions({ canCreateOrEdit: true });
        setPermissionsLoaded(true);
      }

      // Add a timeout as backup
      const timeout = setTimeout(() => {
        console.log('Permission check timeout - proceeding with default permissions');
        if (!permissionsLoaded) {
          setPermissions({ canCreateOrEdit: true });
          setPermissionsLoaded(true);
        }
      }, 1000); // 5 second timeout

      return () => clearTimeout(timeout);
    }
  }, [isOpen, corporateAccount, userId, projectId, login_token]);

  useEffect(() => {
    if (isOpen && inputRef.current && permissions.canCreateOrEdit) {
      inputRef.current.focus();
    }
  }, [isOpen, permissions.canCreateOrEdit]);

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && productToEdit) {
        setProductName(productToEdit.product_name);
        setProductDescription(productToEdit.product_description);
        setProductCompany(productToEdit.product_company);
        setProductVersion(productToEdit.product_version);
      } else {
        setProductName('');
        setProductDescription('');
        setProductCompany('');
        setProductVersion('');
      }
    } else {
      // Reset states when dialog closes
      setProductName('');
      setProductDescription('');
      setProductCompany('');
      setProductVersion('');
      setError('');
      setSuccessMessage('');
      setPermissionsLoaded(false);
    }
  }, [isOpen, isEditMode, productToEdit]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA' && !isSubmitting && isFormValid() && permissions.canCreateOrEdit) {
      handleSubmit(e);
    }
  };

  const isFormValid = () => {
    return productName.trim() && productDescription.trim() && productCompany.trim() && productVersion.trim();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check permissions before submitting
    if (!permissions.canCreateOrEdit) {
      setError('Insufficient permissions to perform this action');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccessMessage('');

    const endpoint = isEditMode ?
      appConfig.getUrl('/update_product') :
      appConfig.getUrl('/create_product');

    const requestBody = isEditMode ?
      {
        corporate_account: corporateAccount,
        project_id: projectId,
        product_id: productToEdit.product_id,
        product_name: productName,
        product_description: productDescription,
        product_company: productCompany,
        product_version: productVersion,
        status: "Active"
      } :
      {
        corporate_account: corporateAccount,
        project_id: projectId,
        product_name: productName,
        product_description: productDescription,
        product_company: productCompany,
        product_version: productVersion,
        status: "Active"
      };

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.status === 'Success') {
        setSuccessMessage(data.status_description || `Product ${isEditMode ? 'updated' : 'created'} successfully`);

        setTimeout(() => {
          onSubmit();

          if (isEditMode) {
            setTimeout(() => onClose(), 5000);
          } else {
            setProductName('');
            setProductDescription('');
            setProductCompany('');
            setProductVersion('');
            if (inputRef.current) {
              inputRef.current.focus();
            }
          }
        }, 3000);
      } else {
        setError(data.status_description || `Failed to ${isEditMode ? 'update' : 'create'} product`);
      }
    } catch (err) {
      setError(`An error occurred while ${isEditMode ? 'updating' : 'creating'} the product`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Show loading state while checking permissions with timeout
  if (!permissionsLoaded) {
    return (
      <div style={DialogStyles.overlay}>
        <div style={DialogStyles.dialog}>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div>Checking permissions...</div>
            <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
              If this takes too long, the dialog will proceed automatically.
            </div>
            <button
              onClick={() => {
                console.log('User clicked proceed anyway');
                setPermissions({ canCreateOrEdit: true });
                setPermissionsLoaded(true);
              }}
              style={{
                backgroundColor: '#FFFDD0',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                marginTop: '15px'
              }}
            >
              Proceed Anyway
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show permission denied message if user doesn't have access
  if (!permissions.canCreateOrEdit) {
    return (
      <div style={DialogStyles.overlay}>
        <div style={DialogStyles.dialog}>
          <h2 style={DialogStyles.title}>
            Access Denied
          </h2>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p>You don't have permission to {isEditMode ? 'edit' : 'create'} products.</p>
            <button
              onClick={onClose}
              style={{
                backgroundColor: '#FFFDD0',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                marginTop: '20px'
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={DialogStyles.overlay}>
      <div style={DialogStyles.dialog}>
        <h2 style={DialogStyles.title}>
          {isEditMode ? 'Edit Product' : 'Create New Product'}
        </h2>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>Project:</label>
            <input
              type="text"
              value={currentProject || 'No project selected'}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: '#f5f5f5'
              }}
              disabled
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              Product Name*:
            </label>
            <input
              type="text"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              onKeyPress={handleKeyPress}
              maxLength={200}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
              placeholder="Enter product name"
              ref={inputRef}
              required
              disabled={isSubmitting}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              Product Description*:
            </label>
            <textarea
              value={productDescription}
              onChange={(e) => setProductDescription(e.target.value)}
              maxLength={500}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                minHeight: '80px',
                resize: 'vertical'
              }}
              placeholder="Enter product description"
              required
              disabled={isSubmitting}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              Company*:
            </label>
            <input
              type="text"
              value={productCompany}
              onChange={(e) => setProductCompany(e.target.value)}
              onKeyPress={handleKeyPress}
              maxLength={200}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
              placeholder="Enter company name"
              required
              disabled={isSubmitting}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              Version*:
            </label>
            <input
              type="text"
              value={productVersion}
              onChange={(e) => setProductVersion(e.target.value)}
              onKeyPress={handleKeyPress}
              maxLength={50}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
              placeholder="Enter product version"
              required
              disabled={isSubmitting}
            />
          </div>

          {/* Error and Success Messages */}
          {error && (
            <div className="mb-4 p-2 rounded bg-red-50 text-red-500">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="mb-4 p-2 rounded bg-green-50 text-green-700">
              {successMessage}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                backgroundColor: '#FFFDD0',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              disabled={isSubmitting}
            >
              Close
            </button>
            <button
              type="submit"
              style={{
                padding: '8px 16px',
                backgroundColor: isFormValid() ? '#C8F4DE' : '#f3f4f6',
                color: isFormValid() ? 'black' : '#9ca3af',
                border: 'none',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center'
              }}
              disabled={isSubmitting || !isFormValid()}
            >
              {isSubmitting && (
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #000',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  marginRight: '8px'
                }}></div>
              )}
              {isSubmitting ? 'Processing...' : (isEditMode ? 'Update Product' : (successMessage ? 'Add Another Product' : 'Create Product'))}
            </button>
          </div>

          {/* Additional help text for create mode */}
          {!isEditMode && !successMessage && (
            <p className="text-gray-600 text-sm mt-4 text-center">
              You can continue adding more products. Click 'Close' when finished.
            </p>
          )}
        </form>

        <style jsx>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
};

export default CreateProductDialog;